import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CellClickArgs } from "react-data-grid";
import KaboodleToaster from "components/common/KaboodleToaster";
import { useUpdateDocumentMutation } from "services/documents-api";
import type { DocumentType } from "../DocumentTypes";
import { isSelectableForMetadoc } from "../utils/metadocSelection";
import type {
  DocumentTableDataType,
  DocumentTableProps,
} from "../DocumentTableTypes";

const MAX_METADOC_SOURCE_SIZE_BYTES = 2.5 * 1024 * 1024 * 1024;
const MAX_METADOC_SOURCE_SIZE_LABEL = "2.5 GB";

type UseDocumentTableLogicProps = {
  documents: DocumentType[];
  fileServices: NonNullable<DocumentTableProps["fileServices"]>;
  metaDoc?: boolean;
  selectedDocumentIds?: Set<number>;
  onSelectionChange?: (ids: Set<number>) => void;
};

/**
 * useDocumentTableLogic - Business logic for DocumentTable component
 *
 * - converting documents to table rows
 * - document mutations (CMS toggle, type change, date change, service assignment)
 * - server-side pagination and sorting
 */
export const useDocumentTableLogic = ({
  documents,
  fileServices,
  metaDoc = false,
  selectedDocumentIds = new Set(),
  onSelectionChange,
}: UseDocumentTableLogicProps) => {
  const [updateDocument] = useUpdateDocumentMutation();
  const [rows, setRows] = useState<DocumentTableDataType[]>([]);
  const [updatingDocIds, setUpdatingDocIds] = useState<number[]>([]);
  const documentSizeByIdRef = useRef<Map<number, number>>(new Map());
  const datePickerRefs = useRef<
    Map<number, { setOpen?: (open: boolean) => void } | null>
  >(new Map());

  // convert document IDs to row keys for React Data Grid selection
  const selectedRows = useMemo(() => {
    return new Set(Array.from(selectedDocumentIds));
  }, [selectedDocumentIds]);

  // convert documents to rows
  useEffect(() => {
    const parsedRows: DocumentTableDataType[] = documents.map((doc) => ({
      id: doc.id,
      original_filename: doc.original_filename || "",
      type: doc.type || "",
      created: doc.created,
      preview_proxy_url: doc.preview_proxy_url || null,
      service_type: doc.service_type || null,
      ar_screening_document_type_display:
        doc.ar_screening_document_type_display || null,
      ai_ocr_job_status_display: doc.ai_ocr_job_status_display || null,
      last_ocr_run_at: doc.last_ocr_run_at || null,
      is_cms_submission_document: doc.is_cms_submission_document || false,
      document: doc,
      antivirus_scan_status_verbose: doc.antivirus_scan_status_verbose || null,
      page_count: doc.page_count || null,
    }));
    setRows(parsedRows);

    documents.forEach((doc) => {
      documentSizeByIdRef.current.set(doc.id, doc.file_size_in_bytes ?? 0);
    });
  }, [documents]);

  const handleCellClick = (args: CellClickArgs<DocumentTableDataType>) => {
    if (args.column.key === "actions") return;
  };

  const handleSelectedRowsChange = useCallback(
    (newSelectedRows: Set<string | number>) => {
      if (!onSelectionChange) return;

      const nextDocumentIds = new Set(
        Array.from(newSelectedRows).map((key) => Number(key)),
      );
      const isSelectableMetadocDocument = (documentId: number) => {
        const matchingDocument = documents.find((doc) => doc.id === documentId);
        if (!matchingDocument) return false;
        return isSelectableForMetadoc(matchingDocument);
      };
      const sanitizedDocumentIds = metaDoc
        ? new Set(
            Array.from(nextDocumentIds).filter((documentId) =>
              isSelectableMetadocDocument(documentId),
            ),
          )
        : nextDocumentIds;

      const currentPageIds = new Set(documents.map((doc) => doc.id));
      const removedIds = Array.from(selectedDocumentIds).filter(
        (id) => currentPageIds.has(id) && !sanitizedDocumentIds.has(id),
      );
      const addedIds = Array.from(sanitizedDocumentIds).filter(
        (id) => !selectedDocumentIds.has(id),
      );

      if (addedIds.length === 0 || !metaDoc) {
        const nextSelection = new Set(selectedDocumentIds);
        removedIds.forEach((id) => nextSelection.delete(id));
        addedIds.forEach((id) => nextSelection.add(id));
        onSelectionChange(nextSelection);
        return;
      }

      const getSizeForDocumentId = (documentId: number) =>
        documentSizeByIdRef.current.get(documentId) ?? 0;

      const nextSelection = new Set(selectedDocumentIds);
      removedIds.forEach((id) => nextSelection.delete(id));

      let totalSelectedBytes = Array.from(nextSelection).reduce(
        (sum, id) => sum + getSizeForDocumentId(id),
        0,
      );
      const blockedIds: number[] = [];

      addedIds.forEach((id) => {
        const documentSize = getSizeForDocumentId(id);
        const wouldExceedLimit =
          totalSelectedBytes + documentSize > MAX_METADOC_SOURCE_SIZE_BYTES;

        if (wouldExceedLimit) {
          blockedIds.push(id);
          return;
        }

        totalSelectedBytes += documentSize;
        nextSelection.add(id);
      });

      if (blockedIds.length > 0) {
        KaboodleToaster.error(
          `MetaDoc source documents cannot exceed ${MAX_METADOC_SOURCE_SIZE_LABEL} total.`,
        );
      }

      onSelectionChange(nextSelection);
    },
    [documents, metaDoc, onSelectionChange, selectedDocumentIds],
  );

  const handleToggleCMS = useCallback(
    async (document: DocumentType) => {
      if (updatingDocIds.includes(document.id)) return;

      try {
        setUpdatingDocIds((prev) => [...prev, document.id]);

        await updateDocument({
          documentId: document.id,
          data: {
            is_cms_submission_document: !document.is_cms_submission_document,
          },
        });
      } catch (error) {
        console.error("Error toggling CMS status:", error);
      } finally {
        setUpdatingDocIds((prev) => prev.filter((id) => id !== document.id));
      }
    },
    [updatingDocIds, updateDocument],
  );

  const handleChangeDocumentType = useCallback(
    async (document: DocumentType, newType: string) => {
      if (updatingDocIds.includes(document.id)) return;

      try {
        setUpdatingDocIds((prev) => [...prev, document.id]);

        await updateDocument({
          documentId: document.id,
          data: {
            type: newType,
          },
        });
      } catch (error) {
        console.error("Error changing document type:", error);
      } finally {
        setUpdatingDocIds((prev) => prev.filter((id) => id !== document.id));
      }
    },
    [updatingDocIds, updateDocument],
  );

  const handleChangeDocumentDate = useCallback(
    async (document: DocumentType, newDate: Date | null) => {
      if (updatingDocIds.includes(document.id)) return;

      try {
        setUpdatingDocIds((prev) => [...prev, document.id]);

        await updateDocument({
          documentId: document.id,
          data: {
            document_date: newDate ? newDate.toISOString() : null,
          },
        });
      } catch (error) {
        console.error("Error changing document date:", error);
      } finally {
        setUpdatingDocIds((prev) => prev.filter((id) => id !== document.id));
      }
    },
    [updatingDocIds, updateDocument],
  );

  const handleChangeDocumentService = useCallback(
    async (
      document: DocumentType,
      serviceId: number | null,
      lienId?: number | null,
    ) => {
      if (updatingDocIds.includes(document.id)) return;

      // map service type to the appropriate document field
      const getServiceIdFieldForType = (serviceType: string) => {
        const serviceFieldMap: Record<string, string> = {
          MSA: "msa_id",
          "MSA-111": "msa_id", // MSA-111 uses msa_id
          NTMSA: "msa_id", // non-threshold MSA uses msa_id
          "PRE-MSA": "msa_id", // pre-MSA snapshot uses msa_id
          ARV: "msa_id", // amended review uses msa_id
          LCP: "cost_projection_id",
          MCP: "cost_projection_id",
          ARS: "amended_review_screening_id",
          LIEN: "lien_file", // use lien_file for lien file service association
          DOCS: "document_management_file_id",
          CON: "miscellaneous_service_id",
          AUD: "miscellaneous_service_id",
        };
        return serviceFieldMap[serviceType] || null;
      };

      // != null, not truthiness, since 0 is a valid PK in principle
      const getServiceEntityId = (
        service: NonNullable<DocumentTableProps["fileServices"]>[0],
      ) => {
        if (service.msa != null) return service.msa;
        if (service.costprojection != null) return service.costprojection;
        if (service.lien_file != null) return service.lien_file;
        if (service.document_management_file != null)
          return service.document_management_file;
        if (service.amended_review_screening != null)
          return service.amended_review_screening;
        // for other types, we might not have the entity ID, so return null
        return null;
      };

      try {
        setUpdatingDocIds((prev) => [...prev, document.id]);

        // clear all service-related foreign key fields
        const data: Record<string, number | null> = {
          msa_id: null,
          cost_projection_id: null,
          amended_review_screening_id: null,
          lien_file: null,
          lien_id: null,
          document_management_file_id: null,
          miscellaneous_service_id: null,
          service_id: null,
        };

        if (serviceId != null) {
          const selectedService = fileServices.find((s) => s.id === serviceId);
          if (!selectedService) return;

          const fieldName = getServiceIdFieldForType(selectedService.type);
          if (!fieldName) {
            console.error("Unknown service type:", selectedService.type);
            return;
          }

          const entityId = getServiceEntityId(selectedService);
          if (entityId != null) {
            // Set the new service field
            data[fieldName] = entityId;

            // If a specific lien is selected, set lien_id as well
            if (lienId != null) {
              data.lien_id = lienId;
            }
          } else {
            // No real record yet (unopened file); tag it as pending,
            // opening the file later backfills the real FK
            data.service_id = selectedService.id;
          }
        }

        await updateDocument({
          documentId: document.id,
          data,
        });
      } catch (error) {
        console.error("Error changing document service:", error);
      } finally {
        setUpdatingDocIds((prev) => prev.filter((id) => id !== document.id));
      }
    },
    [updateDocument, updatingDocIds, fileServices],
  );

  return {
    // state
    rows,
    setRows,
    selectedRows,
    updatingDocIds,
    datePickerRefs,
    // handlers
    handleSelectedRowsChange,
    handleToggleCMS,
    handleChangeDocumentType,
    handleChangeDocumentDate,
    handleChangeDocumentService,
    handleCellClick,
  };
};
